/**
 * What is this running on, and how much can it carry?
 *
 * WHY THIS EXISTS
 * The scene was built for a desktop GPU and every number in it says so: a
 * 240x240 wave tile is 115,000 triangles evaluating six sine waves per vertex
 * every frame, shadows are on, and the renderer draws at up to twice the CSS
 * resolution. On a laptop that is free. On a phone it is a slideshow that gets
 * hot, and the 3x device pixel ratio of a modern handset makes it four times
 * worse than the same scene on the same GPU at 1x.
 *
 * More importantly the CONTROLS were built for a keyboard, and a phone has
 * none. That is not a quality setting, it is a different product surface — see
 * input/TouchControls.js — and the decision of which one to show has to be made
 * somewhere honest rather than by sniffing the user agent string.
 *
 * Pure: everything comes in as an argument so it can be driven from a test with
 * no browser at all. `readEnvironment()` is the only part that touches globals.
 */

/** Rendering tiers, cheapest first. */
export const TIER = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
}

/**
 * Per-tier settings. These are the numbers the scene actually reads.
 *
 * `oceanSegments` is the one that matters most: the wave tile is the single
 * heaviest thing in the frame and its cost is the square of this number. 240 to
 * 96 is not a 60% saving, it is an 84% one.
 */
export const TIER_SETTINGS = {
  [TIER.LOW]: {
    maxPixelRatio: 1.5,
    shadows: false,
    oceanSegments: 96,
    cloudClumps: 10,
    // A phone's screen is small enough that the far end of the wave tile is a
    // couple of pixels wide. Shrinking it costs nothing visible and keeps the
    // triangles that remain where they can be seen.
    oceanTileM: 4200,
  },
  [TIER.MEDIUM]: {
    maxPixelRatio: 2,
    shadows: true,
    oceanSegments: 160,
    cloudClumps: 14,
    oceanTileM: 5600,
  },
  [TIER.HIGH]: {
    maxPixelRatio: 2,
    shadows: true,
    oceanSegments: 240,
    cloudClumps: 20,
    oceanTileM: 7000,
  },
}

/**
 * Read what the browser will tell us. The only impure function here.
 *
 * @returns {{coarsePointer:boolean, maxTouchPoints:number, pixelRatio:number,
 *            shortSideCss:number, cores:number, memoryGb:number|null}}
 */
export function readEnvironment(win = globalThis) {
  const mm = win.matchMedia?.bind(win)
  return {
    // `pointer: coarse` is the honest question — "is the primary input a
    // finger" — and it is the one thing in this list that is not a proxy for
    // something else. User agent sniffing gets a laptop with a touchscreen
    // wrong in one direction and a tablet with a keyboard wrong in the other.
    coarsePointer: !!mm?.('(pointer: coarse)')?.matches,
    maxTouchPoints: win.navigator?.maxTouchPoints ?? 0,
    pixelRatio: win.devicePixelRatio ?? 1,
    shortSideCss: Math.min(win.innerWidth ?? 1280, win.innerHeight ?? 800),
    cores: win.navigator?.hardwareConcurrency ?? 4,
    // Chrome only, and absent on iOS. Null means "did not say", not "none".
    memoryGb: win.navigator?.deviceMemory ?? null,
  }
}

/**
 * Does this device need on-screen controls?
 *
 * Deliberately generous: a laptop with a touchscreen gets them too, and that is
 * fine — they sit out of the way and the keyboard keeps working. The failure
 * that matters is the other one, a phone with no way to lower the gear.
 *
 * BUT THIS IS A PREDICTION, AND PREDICTIONS ARE WRONG SOMETIMES.
 * `pointer: coarse` is false on Android browsers switched to "Desktop site",
 * on a few Windows tablets, and in some in-app webviews. When it is wrong the
 * trainer shows no controls at all, and a phone user is left tapping a screen
 * where nothing happens — with nothing on it to explain why or to turn them on.
 *
 * So this is only the opening guess. `observedTouch()` below is the correction,
 * and it is evidence rather than a guess: a device that emits a touch event IS
 * a touch device, whatever it said about itself beforehand.
 */
export function needsTouchControls(env) {
  return env.coarsePointer || env.maxTouchPoints > 0
}

/**
 * An explicit override, from the URL.
 *
 * `?touch=1` forces the controls on, `?touch=0` off. It exists so that a device
 * this file guesses wrong about is one query string away from working, rather
 * than needing a new deploy — and so that "does it work on your phone" has an
 * answer that does not depend on my being right about your phone.
 *
 * @returns {boolean|null} null when the URL says nothing
 */
export function touchOverride(search = '') {
  const m = /[?&]touch=([^&]*)/.exec(search)
  if (!m) return null
  const v = decodeURIComponent(m[1]).toLowerCase()
  if (v === '0' || v === 'false' || v === 'off' || v === 'no') return false
  return true
}

/**
 * Was that a finger?
 *
 * The one signal that cannot be wrong. Wire it to the first pointer event that
 * arrives and stop guessing.
 */
export function observedTouch(event) {
  return event?.pointerType === 'touch' || event?.pointerType === 'pen'
}

/**
 * How hard can this device be pushed?
 *
 * The signals are all weak individually, which is why several are used. Core
 * count is the least misleading one available — phones report 4 to 8 the same
 * as laptops do — so it is combined with screen size and pixel ratio, and the
 * product of the last two is what the GPU actually has to fill.
 */
export function pickTier(env) {
  // Total pixels the GPU is asked for, in millions, at the ratio we would use.
  const ratio = Math.min(env.pixelRatio, 2)
  const megapixels = ((env.shortSideCss * ratio) ** 2 * 1.8) / 1e6

  if (env.memoryGb !== null && env.memoryGb <= 4) return TIER.LOW
  // A small screen with a big pixel ratio is a phone, whatever it claims about
  // its cores.
  if (env.shortSideCss < 500 && env.pixelRatio >= 2) return TIER.LOW
  // Core count is a weak signal on its own. A desktop in a VM, or an older
  // four-core machine with a perfectly capable GPU, reports the same 4 as a
  // cheap handset — so a big screen driven by a mouse is floored at medium
  // rather than dropped to the bottom tier on that number alone.
  const roomyDesktop = !env.coarsePointer && env.shortSideCss >= 800
  if (env.cores <= 4) return roomyDesktop ? TIER.MEDIUM : TIER.LOW
  // A big tablet is not a phone. An 11-inch iPad in landscape is four
  // megapixels on an M-series GPU, which carries the medium tier comfortably —
  // the threshold is here to catch a phone that lied about its cores, not to
  // demote everything with a touchscreen.
  if (env.coarsePointer && megapixels > 6) return TIER.LOW
  if (env.coarsePointer || env.cores <= 6) return TIER.MEDIUM
  return TIER.HIGH
}

/** Everything the rest of the app wants to know, in one object. */
export function detect(win = globalThis) {
  const env = readEnvironment(win)
  const tier = pickTier(env)
  const forced = touchOverride(win.location?.search ?? '')
  return {
    env,
    tier,
    touch: forced ?? needsTouchControls(env),
    /** True when the URL decided, so the log can say the guess was overridden. */
    touchForced: forced !== null,
    /**
     * `?touch=0` means OFF and stays off. Without this the first tap turned the
     * controls back on through `observedTouch`, so the one flag whose entire
     * job is to force the keyboard path on a touchscreen could not survive
     * being touched.
     */
    touchLocked: forced === false,
    settings: TIER_SETTINGS[tier],
  }
}
